import { useState, useRef } from "react";
import { Upload, X, FileText, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  { value: "standalone", label: "Standalone Price Desk" },
  { value: "price_desk_estimate", label: "Price Desk + Estimate" },
  { value: "price_desk_review", label: "Price Desk + Tender Red Review" },
  { value: "not_sure", label: "Not sure - discuss with me" }
];

const packageCategories = [
  { value: "materials_supply", label: "Materials / Supply" },
  { value: "civil_subcontractors", label: "Civil Subcontractors" },
  { value: "building_subcontractors", label: "Building Subcontractors" },
  { value: "plant_equipment", label: "Plant & Equipment" },
  { value: "cartage_disposal", label: "Cartage & Disposal" },
  { value: "specialist_systems", label: "Specialist Systems" },
  { value: "other", label: "Other" }
];

const pricingStatusOptions = [
  { value: "not_started", label: "Not yet sent to suppliers" },
  { value: "some_sent", label: "Some requests already sent" },
  { value: "quotes_received", label: "Quotes already received" },
  { value: "urgent", label: "Urgent / Close to submission" }
];

const timezones = [
  { value: "NZST", label: "NZ (NZST/NZDT)" },
  { value: "AEST", label: "AU Eastern (AEST/AEDT)" },
  { value: "ACST", label: "AU Central (ACST/ACDT)" },
  { value: "AWST", label: "AU Western (AWST)" }
];

export default function PriceDeskForm() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    project_name: "",
    project_location: "",
    country: "",
    closing_date: "",
    closing_time: "",
    closing_timezone: "NZST",
    service_combination: [],
    package_categories: [],
    package_count: "",
    pricing_status: "",
    notes: "",
    privacy_consent: false
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const ALLOWED_EXTENSIONS = ['.pdf', '.xlsx', '.docx', '.csv', '.zip'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleMultiSelect = (name, value) => {
    setFormData((prev) => {
      const current = prev[name] || [];
      if (current.includes(value)) {
        return { ...prev, [name]: current.filter((v) => v !== value) };
      } else {
        return { ...prev, [name]: [...current, value] };
      }
    });
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    addFiles(newFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = [];
    const rejectedFiles = [];

    newFiles.forEach((file) => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      
      // Check extension
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        rejectedFiles.push(`${file.name} (invalid type)`);
        return;
      }
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        rejectedFiles.push(`${file.name} (exceeds 50MB)`);
        return;
      }
      
      // Check for executable content (basic check)
      if (ext === '.exe' || ext === '.bat' || ext === '.cmd' || ext === '.sh') {
        rejectedFiles.push(`${file.name} (not allowed)`);
        return;
      }

      validFiles.push(file);
    });

    if (rejectedFiles.length > 0) {
      toast.error(`Some files rejected: ${rejectedFiles.join(', ')}`);
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.company.trim()) newErrors.company = "Company is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.project_name.trim()) newErrors.project_name = "Project name is required";
    if (!formData.project_location.trim()) newErrors.project_location = "Project location is required";
    if (!formData.country) newErrors.country = "Please select NZ or Australia";
    if (!formData.closing_date) newErrors.closing_date = "Closing date is required";
    if (formData.service_combination.length === 0) {
      newErrors.service_combination = "Please select at least one service option";
    }
    if (formData.package_categories.length === 0) {
      newErrors.package_categories = "Please select at least one package category";
    }
    if (!formData.pricing_status) newErrors.pricing_status = "Please select current pricing status";
    if (!formData.privacy_consent) newErrors.privacy_consent = "Please accept the privacy consent";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Map to existing quote request fields
      formDataToSend.append("name", formData.name);
      formDataToSend.append("company", formData.company);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone || "");
      formDataToSend.append("location", formData.country === "nz" ? "NZ" : "AU");
      formDataToSend.append("project_type", formData.project_name);
      formDataToSend.append("service_type", "price_desk"); // Key identifier
      formDataToSend.append("timeframe", formData.closing_date + (formData.closing_time ? ` ${formData.closing_time}` : "") + ` (${formData.closing_timezone})`);
      
      // Compile notes with all Price Desk specific fields
      const notesContent = [
        `PROJECT: ${formData.project_name}`,
        `LOCATION: ${formData.project_location}`,
        `COUNTRY: ${formData.country === "nz" ? "New Zealand" : "Australia"}`,
        `CLOSING: ${formData.closing_date} ${formData.closing_time || ""} (${formData.closing_timezone})`,
        `SERVICE: ${formData.service_combination.join(", ")}`,
        `PACKAGES: ${formData.package_categories.join(", ")}`,
        formData.package_count ? `ESTIMATED PACKAGE COUNT: ${formData.package_count}` : "",
        `PRICING STATUS: ${formData.pricing_status}`,
        "",
        "SCOPE/NOTES:",
        formData.notes || "(none provided)"
      ].filter(Boolean).join("\n");
      
      formDataToSend.append("notes", notesContent);

      // Add files
      files.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const response = await axios.post(`${API}/quote-request`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        setReferenceNumber(response.data.id?.slice(0, 8).toUpperCase() || "");
        setIsSuccess(true);
        toast.success("Price Desk request submitted successfully!");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.response?.data?.detail || "Failed to submit. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section
        id="price-desk-form"
        className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
        data-testid="price-desk-form-section"
      >
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-white border-4 border-black p-12">
            <CheckCircle size={64} className="mx-auto mb-6 text-black" />
            <h2 className="font-heading font-bold text-3xl uppercase mb-4">
              Request Received
            </h2>
            {referenceNumber && (
              <p className="font-mono text-lg mb-4">
                Reference: <strong>{referenceNumber}</strong>
              </p>
            )}
            <p className="font-body text-zinc-600 mb-8">
              Thanks—your Price Desk request has been received. Blackline will review the scope, closing date and information provided before confirming the pricing plan and next step.
            </p>
            <button
              onClick={() => {
                setIsSuccess(false);
                setFormData({
                  name: "",
                  company: "",
                  email: "",
                  phone: "",
                  project_name: "",
                  project_location: "",
                  country: "",
                  closing_date: "",
                  closing_time: "",
                  closing_timezone: "NZST",
                  service_combination: [],
                  package_categories: [],
                  package_count: "",
                  pricing_status: "",
                  notes: "",
                  privacy_consent: false
                });
                setFiles([]);
              }}
              className="bg-black text-white h-12 px-8 uppercase font-bold tracking-widest border-2 border-black btn-brutal"
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="price-desk-form"
      className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
      data-testid="price-desk-form-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-12 md:mb-16 text-center">
          <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
            Price Desk Request
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4">
            Start Your Request
          </h2>
          <p className="font-body text-zinc-600 mt-4 max-w-xl mx-auto">
            Tell us about your project and pricing requirements. We'll review and confirm the plan before starting.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border-4 border-black p-6 md:p-10"
          data-testid="price-desk-form"
        >
          {/* Contact Details */}
          <div className="mb-8">
            <h3 className="font-heading font-bold text-lg uppercase mb-4 pb-2 border-b-2 border-black">
              Contact Details
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="pd-name" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Name *
                </label>
                <input
                  type="text"
                  id="pd-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full h-12 px-4 border-2 ${errors.name ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none`}
                  data-testid="input-name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="pd-company" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Company *
                </label>
                <input
                  type="text"
                  id="pd-company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className={`w-full h-12 px-4 border-2 ${errors.company ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none`}
                  data-testid="input-company"
                />
                {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
              </div>

              <div>
                <label htmlFor="pd-email" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Email *
                </label>
                <input
                  type="email"
                  id="pd-email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full h-12 px-4 border-2 ${errors.email ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none`}
                  data-testid="input-email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="pd-phone" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Phone
                </label>
                <input
                  type="tel"
                  id="pd-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                  data-testid="input-phone"
                />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div className="mb-8">
            <h3 className="font-heading font-bold text-lg uppercase mb-4 pb-2 border-b-2 border-black">
              Project Details
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="pd-project-name" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Project Name *
                </label>
                <input
                  type="text"
                  id="pd-project-name"
                  name="project_name"
                  value={formData.project_name}
                  onChange={handleInputChange}
                  className={`w-full h-12 px-4 border-2 ${errors.project_name ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none`}
                  data-testid="input-project-name"
                />
                {errors.project_name && <p className="text-red-500 text-sm mt-1">{errors.project_name}</p>}
              </div>

              <div>
                <label htmlFor="pd-project-location" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Project Location *
                </label>
                <input
                  type="text"
                  id="pd-project-location"
                  name="project_location"
                  value={formData.project_location}
                  onChange={handleInputChange}
                  placeholder="City / Region"
                  className={`w-full h-12 px-4 border-2 ${errors.project_location ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none`}
                  data-testid="input-project-location"
                />
                {errors.project_location && <p className="text-red-500 text-sm mt-1">{errors.project_location}</p>}
              </div>

              <div>
                <label htmlFor="pd-country" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Country *
                </label>
                <select
                  id="pd-country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={`w-full h-12 px-4 border-2 ${errors.country ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer`}
                  data-testid="select-country"
                >
                  <option value="">Select country</option>
                  <option value="nz">New Zealand</option>
                  <option value="au">Australia</option>
                </select>
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </div>

              <div className="md:col-span-2 grid md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="pd-closing-date" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                    Tender Closing Date *
                  </label>
                  <input
                    type="date"
                    id="pd-closing-date"
                    name="closing_date"
                    value={formData.closing_date}
                    onChange={handleInputChange}
                    className={`w-full h-12 px-4 border-2 ${errors.closing_date ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none`}
                    data-testid="input-closing-date"
                  />
                  {errors.closing_date && <p className="text-red-500 text-sm mt-1">{errors.closing_date}</p>}
                </div>

                <div>
                  <label htmlFor="pd-closing-time" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                    Closing Time
                  </label>
                  <input
                    type="time"
                    id="pd-closing-time"
                    name="closing_time"
                    value={formData.closing_time}
                    onChange={handleInputChange}
                    className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                    data-testid="input-closing-time"
                  />
                </div>

                <div>
                  <label htmlFor="pd-timezone" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                    Timezone
                  </label>
                  <select
                    id="pd-timezone"
                    name="closing_timezone"
                    value={formData.closing_timezone}
                    onChange={handleInputChange}
                    className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer"
                    data-testid="select-timezone"
                  >
                    {timezones.map((tz) => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Service Requirements */}
          <div className="mb-8">
            <h3 className="font-heading font-bold text-lg uppercase mb-4 pb-2 border-b-2 border-black">
              Service Requirements
            </h3>
            
            <div className="mb-6">
              <label className="font-heading font-bold text-sm uppercase tracking-wider mb-3 block">
                Service Required * <span className="font-normal text-zinc-500">(select all that apply)</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                {serviceOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-colors ${
                      formData.service_combination.includes(option.value)
                        ? 'border-black bg-zinc-100'
                        : 'border-zinc-300 hover:border-black'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.service_combination.includes(option.value)}
                      onChange={() => handleMultiSelect('service_combination', option.value)}
                      className="w-5 h-5 border-2 border-black"
                    />
                    <span className="font-body text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.service_combination && <p className="text-red-500 text-sm mt-1">{errors.service_combination}</p>}
            </div>

            <div className="mb-6">
              <label className="font-heading font-bold text-sm uppercase tracking-wider mb-3 block">
                Pricing Packages Required * <span className="font-normal text-zinc-500">(select all that apply)</span>
              </label>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {packageCategories.map((cat) => (
                  <label
                    key={cat.value}
                    className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-colors ${
                      formData.package_categories.includes(cat.value)
                        ? 'border-black bg-zinc-100'
                        : 'border-zinc-300 hover:border-black'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.package_categories.includes(cat.value)}
                      onChange={() => handleMultiSelect('package_categories', cat.value)}
                      className="w-5 h-5 border-2 border-black"
                    />
                    <span className="font-body text-sm">{cat.label}</span>
                  </label>
                ))}
              </div>
              {errors.package_categories && <p className="text-red-500 text-sm mt-1">{errors.package_categories}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="pd-package-count" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Approximate Number of Packages
                </label>
                <input
                  type="text"
                  id="pd-package-count"
                  name="package_count"
                  value={formData.package_count}
                  onChange={handleInputChange}
                  placeholder="e.g., 15-20 suppliers"
                  className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                  data-testid="input-package-count"
                />
              </div>

              <div>
                <label htmlFor="pd-pricing-status" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
                  Current Pricing Status *
                </label>
                <select
                  id="pd-pricing-status"
                  name="pricing_status"
                  value={formData.pricing_status}
                  onChange={handleInputChange}
                  className={`w-full h-12 px-4 border-2 ${errors.pricing_status ? 'border-red-500' : 'border-black'} bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer`}
                  data-testid="select-pricing-status"
                >
                  <option value="">Select status</option>
                  {pricingStatusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.pricing_status && <p className="text-red-500 text-sm mt-1">{errors.pricing_status}</p>}
              </div>
            </div>
          </div>

          {/* Scope Description */}
          <div className="mb-8">
            <label htmlFor="pd-notes" className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block">
              Project / Scope Description
            </label>
            <textarea
              id="pd-notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full p-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none resize-none"
              placeholder="Brief description of the project, scope of work, and any specific pricing requirements..."
              data-testid="textarea-notes"
            />
          </div>

          {/* File Upload */}
          <div className="mb-8">
            <label htmlFor="pd-file-upload" className="font-heading font-bold text-sm uppercase tracking-wider mb-3 block">
              Upload Documents
            </label>
            <div
              className={`border-2 border-dashed border-black p-8 text-center cursor-pointer transition-colors ${
                dragOver ? "bg-zinc-100" : "bg-zinc-50 hover:bg-zinc-100"
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="file-drop-zone"
            >
              <Upload size={32} className="mx-auto mb-3 text-zinc-500" />
              <p className="font-body text-zinc-600 mb-2">
                Drag & drop files here, or click to browse
              </p>
              <p className="font-mono text-xs text-zinc-400">
                PDF, XLSX, DOCX, CSV, ZIP • Max 50MB each
              </p>
              <input
                type="file"
                id="pd-file-upload"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.xlsx,.docx,.csv,.zip"
                multiple
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border-2 border-black bg-zinc-50"
                    data-testid={`file-item-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={20} />
                      <span className="font-mono text-sm truncate max-w-xs">
                        {file.name}
                      </span>
                      <span className="font-mono text-xs text-zinc-400">
                        ({(file.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-zinc-200"
                      data-testid={`remove-file-${index}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Privacy Consent */}
          <div className="mb-8 p-4 border-2 border-black bg-zinc-50">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="privacy_consent"
                checked={formData.privacy_consent}
                onChange={handleInputChange}
                className="w-5 h-5 border-2 border-black mt-0.5 flex-shrink-0"
                data-testid="checkbox-consent"
              />
              <span className="font-body text-sm">
                I consent to Blackline Estimating contacting me about this request and storing the information provided in accordance with the{" "}
                <Link to="/privacy" className="underline">Privacy Policy</Link>. I understand that supplier contact will only occur after my approval. *
              </span>
            </label>
            {errors.privacy_consent && <p className="text-red-500 text-sm mt-2">{errors.privacy_consent}</p>}
          </div>

          {/* Submit */}
          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-black text-white h-14 px-12 uppercase font-bold tracking-widest border-2 border-black btn-brutal flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Price Desk Request
                  <Upload size={20} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
